import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { GlobalTips } from "@/components/GlobalTips";

import { AuthProvider } from "@/contexts/AuthContext";
import { BillingProvider } from "@/contexts/BillingContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { GuestProvider } from "@/contexts/GuestContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Header } from "@/components/Header";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieBanner } from "@/components/CookieBanner";
import { PageviewTracker } from "@/components/PageviewTracker";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import SummaryDetail from "@/pages/SummaryDetail";
import Flashcards from "@/pages/Flashcards";
import FlashcardStudy from "@/pages/FlashcardStudy";
import PublicFlashcards from "@/pages/PublicFlashcards";
import PublicMindmap from "@/pages/PublicMindmap";
import PublicQuiz from "@/pages/PublicQuiz";
import SchoolOffer from "@/pages/SchoolOffer";
import Settings from "@/pages/Settings";
import PublicSummary from "@/pages/PublicSummary";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import Upgrade from "@/pages/Upgrade";
import About from "@/pages/About";
import Etudiants from "@/pages/Etudiants";
import Dys from "@/pages/Dys";
import Oeuvres from "@/pages/Oeuvres";
import Oeuvre from "@/pages/Oeuvre";
import Education from "@/pages/Education";
import Enseignants from "@/pages/Enseignants";
import CasPratiques from "@/pages/CasPratiques";
import Privacy from "@/pages/Privacy";
import Cgv from "@/pages/Cgv";
import ResetPassword from "@/pages/ResetPassword";
import DeleteAccount from "@/pages/DeleteAccount";
import Admin from "@/pages/Admin";
import ReadingList from "@/pages/ReadingList";
import Demo from "@/pages/Demo";
import Showcase from "@/pages/Showcase";
import { Footer, LegalLine } from "@/components/Footer";
import { MobileTabBar } from "@/components/MobileTabBar";

const AppShell = ({ children }) => (
  <div className="App min-h-screen flex flex-col pb-16 md:pb-0">
    <OfflineBanner />
    <Header />
    <main className="flex-1">
      <ErrorBoundary
        fallback={(msg, reset) => (
          <div className="max-w-xl mx-auto p-8 mt-12 border border-[#002FA7] bg-[#002FA7]/5 text-center">
            <div className="sux-marker mb-3 text-[#002FA7]">Erreur inattendue</div>
            <h2 className="text-2xl font-black mb-3" style={{ fontFamily: "Outfit" }}>
              Un petit souci au chargement de cette page.
            </h2>
            <p className="font-mono text-xs text-muted-foreground mb-6 break-words">
              {msg}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={reset}
                className="font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] transition-colors"
              >
                Réessayer
              </button>
              <a
                href="/"
                className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              >
                Accueil
              </a>
            </div>
          </div>
        )}
      >
        {children}
      </ErrorBoundary>
    </main>
    <Footer />
    <MobileTabBar />
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <GuestProvider>
        <AuthProvider>
          <BillingProvider>
            <BrowserRouter>
          <ScrollToTop />
          <PageviewTracker />
          <CookieBanner />
          <GlobalTips />
          <Toaster
            position="top-center"
            theme="system"
            toastOptions={{
              style: {
                borderRadius: 0,
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
              },
            }}
          />
        <Routes>
          <Route path="/login" element={<><Login /><LegalLine className="text-center px-6 py-4 border-t border-border" /></>} />
          <Route path="/signup" element={<><Signup /><LegalLine className="text-center px-6 py-4 border-t border-border" /></>} />
          <Route path="/about" element={<About />} />
          <Route path="/etudiants" element={<Etudiants />} />
          <Route path="/dys" element={<AppShell><Dys /></AppShell>} />
          <Route path="/oeuvres" element={<AppShell><Oeuvres /></AppShell>} />
          <Route path="/oeuvres/:slug" element={<AppShell><Oeuvre /></AppShell>} />
          <Route path="/education" element={<Education />} />
          <Route path="/enseignants" element={<Enseignants />} />
          <Route path="/enseignants/cas-pratiques" element={<CasPratiques />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cgv" element={<Cgv />} />
          <Route path="/etablissements/devis" element={<AppShell><SchoolOffer /></AppShell>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/s/:id" element={<PublicSummary />} />
          <Route path="/c/:id" element={<PublicFlashcards />} />
          <Route path="/m/:id" element={<PublicMindmap />} />
          <Route path="/q/:id" element={<PublicQuiz />} />
          <Route path="/cartes" element={<ProtectedRoute><AppShell><Flashcards /></AppShell></ProtectedRoute>} />
          <Route path="/cartes/:id" element={<ProtectedRoute><AppShell><FlashcardStudy /></AppShell></ProtectedRoute>} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/showcase" element={<Showcase />} />
          <Route
            path="/"
            element={
              <AppShell>
                <Dashboard />
              </AppShell>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <AppShell>
                  <History />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/history/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SummaryDetail />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Settings />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invite"
            element={<Navigate to="/" replace />}
          />
          <Route
            path="/reading-list"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ReadingList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BillingSuccess />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BillingCancel />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Admin />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/upgrade"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Upgrade />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
          </BillingProvider>
        </AuthProvider>
      </GuestProvider>
    </ThemeProvider>
  );
}

export default App;
